import React, { Fragment, useCallback, useEffect, useState } from "react";
import { propsStatsMetricsCard } from "constants/metrics";
import { useTranslation } from "react-i18next";
import { Grid, Typography } from "@mui/material";
import { Loading } from "components/common";
import Request from "core/requests";
import { useDispatch, useSelector } from "react-redux";
import { refreshToken } from "store/user.store";
import { logout } from "store/user.store";
import { openSnackbar } from "store/slices/snackbar";
import _ from "lodash";
import StatsMetricCard from "components/cards/stats/metrics/StatsMetricCard.component";
import LinearProgressStats from "components/cards/stats/linearProgress/LineaProgressStats.component";
import AvgStatsComponent from "components/cards/stats/avg/AvgStats.component";
import LegaliLimitComponent from "components/cards/stats/limit/LegalLimit.component";
import LinearProgressTotDaysStats from "components/cards/stats/linearProgress/LineaProgressTotDaysStats.component";
import { getTimeZoneStandard } from "utils/timezone";
import { DateTime } from "luxon";

const StatsMetricContainer = ({ metric, zoneSelected, refresh, stationIdSelected }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { token, user } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [isFirstRefresh, setIsFirstRefresh] = useState(true);
  const [dataMetric1, setDataMetric1] = useState(null);
  const [dataMetric2, setDataMetric2] = useState(null);

  const renderContentPreviousDay = useCallback(
    (value, data) => {
      const viewAllStats = stationIdSelected === null && zoneSelected?.length >= 2
      if (value)
        switch (value?.endpoint) {
          case "air_temp":
            return (
              <Fragment>
                {!viewAllStats &&
                  <LegaliLimitComponent
                    title={t("Heat Wave")}
                    sm={3.5}
                    status={
                      data?.stats?.hot_wave_status !== null
                        ? data?.stats?.hot_wave_status === 1
                          ? "warning"
                          : data?.stats?.hot_wave_status === 2
                            ? "critic"
                            : "ok"
                        : null
                    }
                    label={
                      data?.stats?.hot_wave_status !== null
                        ? data?.stats?.hot_wave_status === 0
                          ? t("Not in progress")
                          : `${t("In progress")} ${t("from")} ${data?.stats?.hot_wave_days
                          } 
                      ${t("Days").toLocaleLowerCase()}`
                        : null
                    }
                    subLabel={data?.stats?.hot_wave_status !== null
                      && data?.stats?.hot_wave_status !== 0 ? `${`${data?.stats?.hot_wave_status === 2
                        ? `(${t("long wave")})`
                        : `(${t("short wave")})`}`}` : undefined}
                  />}


                <AvgStatsComponent
                  {...data?.data}
                  sm={viewAllStats ? 6 : 5}
                  title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                    })`}
                />
              </Fragment>
            );
          case "air_hum":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data}
                title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                  })`}
              />
            );
          case "CO2":
            return (
              <Fragment>
                {!viewAllStats && <LegaliLimitComponent
                  title={t("CO2 level")}
                  sm={3.5}
                  legalLimit={data?.stats?.level}
                  isCO2={true}
                />}

                <AvgStatsComponent
                  {...data?.data}
                  sm={viewAllStats ? 6 : 5}
                  title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                    })`}
                />
              </Fragment>
            );
          case "lux":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data}
                title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                  })`}
              />
            );
          case "bar_press":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data}
                title={`${t('atmospheric-pressure')} (${propsStatsMetricsCard[value.endpoint].symbol
                  })`}
              />
            );
          case "rain_intensity_peak":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data}
                title={`${t('precipitation-peak')} (${propsStatsMetricsCard[value.endpoint].symbol
                  })`}
              />
            );
          case "rain_intensity":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data}
                title={`${t('Rainfall intensity')} (${propsStatsMetricsCard[value.endpoint].symbol
                  })`}
              />
            );
          case "wind_speed":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data}
                title={`${t('wind-speed')} (${propsStatsMetricsCard[value.endpoint].symbol})`}
              />
            );

          case "pm2_5":
            return (
              <Fragment>
                {!viewAllStats && <LegaliLimitComponent
                  title={t("OMS threshold")}
                  sm={3.5}
                  legalLimit={data?.stats?.over_oms_limit}
                  isFem={true}
                />}

                <AvgStatsComponent
                  {...data?.data}
                  sm={viewAllStats ? 6 : 5}
                  title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                    })`}
                />
              </Fragment>
            );

          case "pm10":
            return (
              <Fragment>
                {!viewAllStats &&
                  <LegaliLimitComponent
                    sm={3.5}
                    title={t("Legal limit")}
                    legalLimit={data?.stats?.over_legal_limit}
                    omsLimit={data?.stats?.over_oms_limit}
                  />
                }
                <AvgStatsComponent
                  {...data?.data}
                  sm={viewAllStats ? 6 : 5}
                  title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                    })`}
                />
              </Fragment>
            );

          case "pcount":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data}
                title={`${t(value?.label)} (${t(propsStatsMetricsCard[value.endpoint].symbol)
                  })`}
              />
            );

          default:
            return null;
        }
    },
    [t, zoneSelected, stationIdSelected]
  );
  const renderContentAnnual = useCallback(
    (value, data) => {
      const viewAllStats = stationIdSelected === null && zoneSelected?.length >= 2

      if (value)
        switch (value?.endpoint) {
          case "air_temp":
            return (
              <Fragment>
                {!viewAllStats &&
                  <LinearProgressStats
                    sm={5}
                    title={"Heat wave days"}
                    days={data?.stats?.hot_wave_days_year}
                    {...propsStatsMetricsCard[value.endpoint]}
                  />}
                {!viewAllStats &&
                  <Grid
                    item
                    container
                    xs={3}
                    alignItems="flex-start"
                    sx={{ minHeight: 80 }}
                  >
                    {data?.stats?.hot_wave_count_short !== null &&
                      data?.stats?.hot_wave_count_long !== null ? (
                      <Fragment>
                        <Grid item xs={12}>
                          <Typography align="center" variant="h6" color="white">
                            {t("Number waves")}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography align="center" variant="h4" color="white">
                            {`${data?.stats?.hot_wave_count_short +
                              data?.stats?.hot_wave_count_long
                              }`}
                          </Typography>
                        </Grid>
                      </Fragment>
                    ) : (
                      <Fragment>
                        <Grid item xs={12}>
                          <Typography align="center" variant="h6" color="white">
                            {t("Number waves")}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} mt={0.8}>
                          <Typography
                            align="center"
                            variant="h6"
                            sx={{ color: "white", opacity: 0.9 }}
                          >
                            {t("Data not available")}
                          </Typography>
                        </Grid>
                      </Fragment>
                    )}
                  </Grid>}

                <AvgStatsComponent
                  sm={4}
                  {...data?.data_year}
                  title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                    })`}
                />
              </Fragment>
            );
          case "CO2":
            return (
              <Fragment>
                {!viewAllStats &&
                  <LinearProgressStats
                    title={"Days with critical CO2 level"}
                    days={data?.stats?.over_level_days_year_count}
                    sm={5}
                    {...propsStatsMetricsCard[value.endpoint]}
                  />}
                <Grid item xs={1} />
                <AvgStatsComponent
                  sm={5}
                  {...data?.data_year}
                  title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                    })`}
                />
              </Fragment>
            );
          case "air_hum":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data_year}
                title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                  })`}
              />
            );
          case "lux":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data_year}
                title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                  })`}
              />
            );
          case "bar_press":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data_year}
                title={`${t('atmospheric-pressure')} (${propsStatsMetricsCard[value.endpoint].symbol
                  })`}
              />
            );
          case "rain_intensity_peak":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data_year}
                title={`${t('precipitation-peak')} (${propsStatsMetricsCard[value.endpoint].symbol
                  })`}
              />
            );
          case "rain_intensity":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data_year}
                title={`${t('Rainfall intensity')} (${propsStatsMetricsCard[value.endpoint].symbol
                  })`}
              />
            );
          case "wind_speed":
            return (
              <AvgStatsComponent
                sm={6}
                {...data?.data_year}
                title={`${t('wind-speed')} (${propsStatsMetricsCard[value.endpoint].symbol
                  })`}
              />
            );

          case "pm2_5":
            return (
              <Fragment>
                {!viewAllStats
                  && <LegaliLimitComponent
                    title={t("Legal limit")}
                    legalLimit={data?.stats?.over_legal_limit_year}
                    omsLimit={data?.stats?.over_oms_limit_year}
                  />}
                <AvgStatsComponent
                  {...data?.data_year}
                  sm={viewAllStats ? 6 : 5}
                  title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                    })`}
                />
              </Fragment>
            );

          case "pm10":
            return (
              <Fragment>
                {!viewAllStats
                  &&
                  <Fragment>
                    <LegaliLimitComponent
                      sm={3.5}
                      title={t("Legal limit")}
                      legalLimit={data?.stats?.over_legal_limit_year}
                      omsLimit={data?.stats?.over_oms_limit_year}
                    />

                    <LinearProgressTotDaysStats
                      sm={5}
                      title={"Daily Limit"}
                      legalDayLimit={data?.stats?.over_legal_limit_days_year}
                      days={data?.stats?.over_legal_limit_days_year_count}
                    />
                  </Fragment>
                }

                <AvgStatsComponent
                  {...data?.data_year}
                  sm={viewAllStats ? 6 : 3.5}
                  title={`${t(value?.label)} (${propsStatsMetricsCard[value.endpoint].symbol
                    })`}
                />
              </Fragment>
            );

          case "pcount":
            return (
              <AvgStatsComponent
                {...data?.data_year}
                title={`${t(value?.label)} (${t(propsStatsMetricsCard[value.endpoint].symbol)})`}
              />
            );
          default:
            return null;
        }
    },
    [t, stationIdSelected, zoneSelected]
  );
  const onFailure = useCallback((error) => {
    dispatch(
      openSnackbar({
        open: true,
        message: error.response?.data?.message ? error.response.data.message : "Errore",

        variant: "alert",
        alert: {
          color: "error",
        },
        close: false,
      })
    );
    setLoading(false);
    setDataMetric1(null);
    setDataMetric2(null);
    // eslint-disable-next-line
  }, []);

  const onError = useCallback(
    (error) => {
      if (isFirstRefresh) {
        setLoading(false);
        if (error?.response?.status === 403) {
          onLogout(error);
        } else {
          if (error?.response?.status !== 401) {
            onFailure(error);
          } else {
            if (isFirstRefresh) {
              dispatch(
                refreshToken({
                  grant_type: "refresh_token",
                  refresh_token: user.refresh_token,
                })
              );
              setIsFirstRefresh(false);
            }
          }
        }
      } else {
        onLogout(error);
      }
    },
    // eslint-disable-next-line
    []
  );

  const onLogout = useCallback((error) => {
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      dispatch(logout());
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setDataMetric1(null);
    setDataMetric2(null);
    setLoading(true)

    if (stationIdSelected) {
      metric?.data?.map((item, index) => {
        Request.stations
          .getStatsStationMetrics({
            metric: item?.endpoint,
            token: token,
            station_id: _.split(stationIdSelected, "-")[0].trim(),
          })
          .then((resp) => {
            if (index !== 0) {
              setDataMetric2(resp);
              setLoading(false);
            } else {
              setDataMetric1(resp);
              if (metric?.data?.length === 1) setLoading(false);
            }
          })
          .catch((error) => {
            setLoading(false);
            onError(error);
          });
        return item;
      });
    } else {
      metric?.data?.map((item, index) => {
        Request.stations
          .getStatsMetrics({
            metric: item?.endpoint,
            token: token,
            path: !_.isEmpty(zoneSelected) ? `?cluster=${zoneSelected}` : "",
          })
          .then((resp) => {
            if (index !== 0) {
              setDataMetric2(resp);
              setLoading(false);
            } else {
              setDataMetric1(resp);
              if (metric?.data?.length === 1) setLoading(false);
            }
          })
          .catch((error) => {
            setLoading(false);
            onError(error);
          });
        return item;
      });
    }

    // eslint-disable-next-line
  }, [metric, zoneSelected, token, refresh, stationIdSelected]);

  return loading ? (
    <Loading />
  ) : (
    <Grid container spacing={2}>
      {metric &&
        metric?.data.map((item, index) => {
          const data = index !== 0 ? dataMetric2 : dataMetric1;
          return (
            item?.endpoint !== "wind_dir_idx" && (
              <Fragment key={item?.endpoint}>
                <Grid item xs={12} sm={6} >
                  <StatsMetricCard
                    label={t("Previous day data")}
                    //time={data?.time}
                    time={data?.time ? `${t('Data relating to')}: 
                    ${DateTime.fromISO(getTimeZoneStandard(data?.time)).toLocaleString(DateTime.DATE_MED, {
                      locale: i18n?.language ? i18n.language : "it",
                    })}` : `${t("Data relating to")}: ${t('unknown')}`}

                    {...data?.data}
                    {...propsStatsMetricsCard[item.endpoint]}
                  >
                    {renderContentPreviousDay(item, data)}
                  </StatsMetricCard>
                </Grid>
                <Grid item xs={12} sm={6} key={index}>
                  <StatsMetricCard
                    label={t("Annual data")}
                    time={data?.time ? `${t('Data relating to')}: 
                    ${DateTime.fromISO(getTimeZoneStandard(data?.time)).year}`
                      : `${t("Data relating to")}: ${t('unknown')}`}
                    {...data?.data_year}
                    {...propsStatsMetricsCard[item.endpoint]}
                  >
                    {renderContentAnnual(item, data)}
                  </StatsMetricCard>
                </Grid>
              </Fragment>
            )
          );
        })}
    </Grid>
  );
};

export default StatsMetricContainer;
